import { Spacer } from '@chakra-ui/react'
import { Box, Flex, Slider, Text } from '@chakra-ui/react'
import { useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import {
  CornerRightDown,
  Download,
  FastForward,
  Info,
  Pause,
  Play,
  Rewind,
  Volume2,
  VolumeX,
} from 'lucide-react'
// import { atomWithStorage } from 'jotai/utils'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactPlayer from 'react-player'
import { useNavigate } from 'react-router'
import IconPbs1_0x from '~/assets/svgs/icon-pbs-1.0x.svg?react'
import IconPbs1_5x from '~/assets/svgs/icon-pbs-1.5x.svg?react'
import IconPbs1_25x from '~/assets/svgs/icon-pbs-1.25x.svg?react'
import IconPbs1_75x from '~/assets/svgs/icon-pbs-1.75x.svg?react'
import IconPbs2_0x from '~/assets/svgs/icon-pbs-2.0x.svg?react'
import { StorageKeys } from '~/atoms/StorageKeys'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { Tooltip } from '~/components/ui/tooltip'
import { useKeyPresses } from '~/hooks/useKeyboard'
import { useOutsideClick } from '~/hooks/useOutsideClick'
import { ApplicationRoutes } from '~/hooks/useRouting'
import { AudioData } from '~/types/AudioPlayerTypes'
import { SettingsType } from '~/types/Settings'
import { Size } from '~/types/ThemeTypes'
import { hasValue } from '~/utils/common'
import {
  convertCallDurationNoMilliseconds,
  formatMilliseconds,
  tripLeadingZeroFromTime,
} from '~/utils/formatter'
import { wasFocusedByPointer } from '~/utils/pointerFocus'
import { castRef } from '~/utils/refUtils'

import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'

import {
  AudioPlayerVariant,
  useAudioPlayerStyle,
} from './componentStyles/AudioPlayer.style'
import { DropdownActionButton } from './Dropdown/DropdownActionButton'
import { LoadingSpinner } from './LoadingSpinner'

const playbackSpeedAtom = atomWithStorage(
  StorageKeys.AudioPlayerPlaybackSpeed,
  0,
)

const playbackSpeeds: PlaybackSpeedIconData[] = [
  {
    speed: 1.0,
    speedText: '1.0x',
    icon: IconPbs1_0x,
    iconWidth: '1.25rem',
  },
  {
    speed: 1.25,
    speedText: '1.25x',
    icon: IconPbs1_25x,
    iconWidth: '2.5rem',
  },
  {
    speed: 1.5,
    speedText: '1.5x',
    icon: IconPbs1_5x,
    iconWidth: '2rem',
  },
  {
    speed: 1.75,
    speedText: '1.75x',
    icon: IconPbs1_75x,
    iconWidth: '2.5rem',
  },
  {
    speed: 2.0,
    speedText: '2.0x',
    icon: IconPbs2_0x,
    iconWidth: '1.25rem',
  },
]

/**
 * Space natively activates these, so the play/pause shortcut must not claim
 * it while one has keyboard focus. A control focused by a click keeps the
 * shortcut, as it did when list rows weren't focusable.
 */
const spaceActivatedControlSelector = [
  'button',
  'select',
  'summary',
  '[role="button"]',
  '[role="checkbox"]',
  '[role="switch"]',
  '[role="radio"]',
  '[role="menuitem"]',
  '[role="menuitemcheckbox"]',
  '[role="menuitemradio"]',
  '[role="option"]',
  '[role="tab"]',
].join(', ')

const isSpaceActivatedControl = (element: Element | null) =>
  element?.matches(spaceActivatedControlSelector) ?? false

const isTextEntry = (element: Element | null) =>
  element?.tagName === 'INPUT' ||
  element?.tagName === 'TEXTAREA' ||
  ((element as HTMLElement | null)?.isContentEditable ?? false)

/** Widgets that move their own value or selection with the arrow keys. */
const arrowKeyWidgetSelector = [
  'select',
  '[role="slider"]',
  '[role="spinbutton"]',
  '[role="radiogroup"]',
  '[role="radio"]',
  '[role="tablist"]',
  '[role="tab"]',
  '[role="menu"]',
  '[role="menubar"]',
  '[role="listbox"]',
  '[role="combobox"]',
  '[role="grid"]',
  '[role="tree"]',
].join(', ')

/** Widgets that jump to an item when a letter is typed. */
const typeaheadWidgetSelector = [
  'select',
  '[role="menu"]',
  '[role="menubar"]',
  '[role="listbox"]',
  '[role="combobox"]',
  '[role="grid"]',
  '[role="tree"]',
].join(', ')

const isInside = (element: Element | null, selector: string) =>
  element?.closest(selector) != null

/** Whether Space with this element focused belongs to the play/pause shortcut. */
const spaceTogglesPlayback = (activeElement: Element | null) =>
  !isTextEntry(activeElement) &&
  !(
    isSpaceActivatedControl(activeElement) &&
    !wasFocusedByPointer(activeElement)
  )

interface AudioPlayerState {
  isMediaReady?: boolean
  isPlaying?: boolean
  isFinishedBuffering?: boolean
  scrubbing?: boolean
  muted?: boolean
  requestedSeekAndPlayTime?: number | null
  keyboardInputEnabled?: boolean
  audioData?: AudioData | null
  volume?: number
  playbackRate?: number | null
}

interface PlaybackSpeedIconData {
  speed: number
  speedText: string
  icon: SVGComponent
  iconWidth: string
}

interface AudioPlayerProps {
  audioUri: string | undefined
  playerTitle?: string
  answerTimestamp?: number
  requestedTimestamp?: number
  disabled?: boolean
  showInfoButton?: boolean
  showInlineInfoButton?: boolean
  showJumpToAnswerButton?: boolean
  showDownloadButton?: boolean
  transcriptionText?: string
  onReady?: () => void
  onLoadError?: () => void
  onValueChanged?: (value: number) => void
  onPlayStateChange?: (isPlaying: boolean) => void
  variant: AudioPlayerVariant
  requestedIsPlaying?: boolean
}
export const AudioPlayer = ({
  audioUri,
  playerTitle,
  answerTimestamp,
  requestedTimestamp,
  disabled,
  showInfoButton = true,
  showJumpToAnswerButton = true,
  showDownloadButton = false,
  showInlineInfoButton = false,
  transcriptionText,
  onReady,
  onLoadError,
  onValueChanged,
  onPlayStateChange: handlePlayStateChange,
  variant,
  requestedIsPlaying = false,
}: AudioPlayerProps) => {
  const [playerPosition, setPlayerPosition] = useState(0)
  const [playerState, setPlayerState] = useState<AudioPlayerState>({})
  const [mediaReady, setMediaReady] = useState(false)
  const [mediaErrored, setMediaErrored] = useState(false)
  const [player, setPlayer] = useState<HTMLVideoElement | null>(null)
  const [selectedPlaybackSpeedIndex, setSelectedPlaybackSpeed] =
    useAtom(playbackSpeedAtom)
  const [playbackSpeedMenuOpen, setPlaybackSpeedMenuState] = useState(false)
  const [isThumbHovered, setIsThumbHovered] = useState(false)

  const boxRef = React.useRef<HTMLDivElement>(null)
  const playerContainerRef = useRef<HTMLDivElement>(null)

  const scrubShortcutLengthMillis = 10 * 1000
  const seekStepMillis = 1000
  const shortScrubtShortcutLengthMillis = 3 * 1000
  const disabledRef = useRef(disabled)
  disabledRef.current = disabled
  // The shortcut itself runs from state after the event; scrolling can only
  // be stopped while the keydown is still being dispatched.
  const preventSpaceScroll = useCallback((e: KeyboardEvent) => {
    if (
      e.key === ' ' &&
      !disabledRef.current &&
      spaceTogglesPlayback(document.activeElement)
    ) {
      e.preventDefault()
    }
  }, [])
  const keyPress = useKeyPresses({ onPress: preventSpaceScroll })
  const navigate = useNavigate()
  const { t } = useTranslation()

  const formattedScrubberPosition = useMemo(() => {
    return tripLeadingZeroFromTime(formatMilliseconds(playerPosition))
  }, [playerPosition])

  const setPlayerRef = (playerRef: HTMLVideoElement) => {
    setPlayer(playerRef)
  }

  const setIsReady = () => {
    setMediaReady(true)
    onReady?.()
  }

  const setIsBuffering = () => {
    setPlayerState({ ...playerState, isFinishedBuffering: false })
  }

  const setDoneBuffering = () => {
    setPlayerState({ ...playerState, isFinishedBuffering: true })
  }

  const onPlayerLoadError = () => {
    setMediaErrored(true)
    onLoadError?.()
  }

  const setDuration = (duration: number) => {
    const milliseconds = Math.floor(duration * 1000)
    setPlayerState({
      ...playerState,
      audioData: {
        seconds: duration,
        milliseconds: milliseconds,
        formattedTime: formatMilliseconds(milliseconds),
      },
    })
  }

  const handleDurationChange = (
    event: React.SyntheticEvent<HTMLVideoElement>,
  ) => {
    const videoElement = event.currentTarget
    const duration = videoElement.duration
    if (duration && isFinite(duration)) {
      setDuration(duration)
    }
  }

  const handleSetPlayerPosition = (position: number) => {
    onValueChanged?.(position)
    setPlayerPosition(position)
  }

  const setProgress = (event: React.SyntheticEvent<HTMLVideoElement>) => {
    const videoElement = event.currentTarget
    const playedSeconds = videoElement.currentTime
    const playedMillis = playedSeconds * 1000
    handleSetPlayerPosition(playedMillis)
    setPlayerState({
      ...playerState,
      scrubbing: false,
    })
  }

  const scrubToMillis = (toMillis: number) => {
    if (toMillis < 0) toMillis = 0
    const clipLengthMillis = playerState.audioData?.milliseconds ?? 0
    if (toMillis > clipLengthMillis) toMillis = clipLengthMillis

    handleSetPlayerPosition(toMillis)
    const secondsVal = toMillis > 0 ? toMillis / 1000 : 0
    if (player) {
      player.currentTime = secondsVal
    }
    setPlayerState({
      ...playerState,
      scrubbing: true,
    })
  }

  const scrubFromDelta = (deltaMillis: number) => {
    scrubToMillis(playerPosition + deltaMillis)
  }

  const setAudioEnded = () => {
    if (!playerState.isPlaying) return

    setPlayerState({
      ...playerState,
      isPlaying: false,
    })
    handleSetPlayerPosition(0)
    if (player) {
      player.currentTime = 0
    }
  }

  const setAudioMuted = (val: boolean) => {
    setPlayerState({
      ...playerState,
      muted: val,
    })
  }

  const setAudioPlaying = (val: boolean) => {
    setPlayerState({
      ...playerState,
      isPlaying: val,
    })
  }

  const handleSliderValueChanged = (val: number) => {
    scrubToMillis(val)
  }

  useEffect(() => {
    if (playerState.isPlaying !== undefined) {
      handlePlayStateChange?.(playerState.isPlaying)
    }
  }, [playerState.isPlaying])

  // if the audioUri changes, pause the audio and reset the position
  useEffect(() => {
    setPlayerPosition(0)
    setAudioPlaying(false)
  }, [audioUri])

  // if the player is disabled, pause the audio
  // user must resume manually
  useEffect(() => {
    if (disabled) {
      setAudioPlaying(false)
    }
  }, [disabled])

  //watches for a requestedTimestamp to be set and handles state updates.
  useEffect(() => {
    if (requestedTimestamp === undefined || !player || !mediaReady) return

    scrubToMillis(requestedTimestamp)
  }, [requestedTimestamp, mediaReady])

  //watches for a requestedIsPlaying to be set and handles state updates.
  useEffect(() => {
    if (!player || !mediaReady) return

    if (requestedIsPlaying !== playerState.isPlaying) {
      setAudioPlaying(requestedIsPlaying)
    }
  }, [mediaReady, requestedIsPlaying])

  //watch keypress change
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent | null) => {
      if (!e?.key) return

      const activeElement = document.activeElement

      if (isTextEntry(activeElement)) {
        return
      }

      const target = e.target instanceof Element ? e.target : activeElement
      // Mute and seek only answer to keys pressed inside this player, so they
      // never act on another widget or shadow a browser shortcut.
      const isPlayerShortcut =
        !e.altKey &&
        !e.ctrlKey &&
        !e.metaKey &&
        (playerContainerRef.current?.contains(target) ?? false)

      const val = e.shiftKey
        ? shortScrubtShortcutLengthMillis
        : scrubShortcutLengthMillis
      switch (e.key) {
        case 'ArrowLeft':
          if (!isPlayerShortcut || isInside(target, arrowKeyWidgetSelector))
            return
          scrubFromDelta(-val)
          break
        case 'ArrowRight':
          if (!isPlayerShortcut || isInside(target, arrowKeyWidgetSelector))
            return
          scrubFromDelta(val)
          break
        case 'm':
          if (!isPlayerShortcut || isInside(target, typeaheadWidgetSelector))
            return
          setAudioMuted(!playerState.muted)
          break
        case ' ':
          if (!spaceTogglesPlayback(activeElement)) return
          setAudioPlaying(!playerState.isPlaying)
          break
        default:
          break
      }
    }

    if (disabled) {
      return
    }

    onKeyDown(keyPress)
  }, [keyPress])

  const handleSetSelectedPlaybackSpeed = (data: PlaybackSpeedIconData) => {
    setPlayerState({
      ...playerState,
      playbackRate: data.speed,
    })
    setSelectedPlaybackSpeed(playbackSpeeds.indexOf(data))
    setPlaybackSpeedMenuState(false)
  }

  const handleInfoButtonPressed = () => {
    setAudioPlaying(false)
    navigate(`${ApplicationRoutes.SETTINGS}/${SettingsType.SHORTCUTS}`)
  }

  const handleDownloadButtonClicked = async () => {
    if (!audioUri) return
    const link = document.createElement('a')
    link.href = audioUri
    link.setAttribute('download', 'voicemail')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  useOutsideClick({
    ref: castRef(boxRef),
    handler: () => {
      setPlaybackSpeedMenuState(false)
    },
  })

  const styles = useAudioPlayerStyle({ variant })

  // Extract layout properties to apply as native props (Chakra v3 requirement)
  const containerProps = {
    flexDirection: styles.container?.flexDirection,
    gap: styles.container?.gap,
  }

  const headerContainerProps = {
    alignItems: styles.headerContainer?.alignItems,
  }

  const controlContainerProps = {
    alignItems: styles.controlContainer?.alignItems,
    gap: styles.controlContainer?.gap,
    justifyContent: styles.controlContainer?.justifyContent,
  }

  const controlButtonsContainerProps = {
    alignItems: styles.controlButtonsContainer?.alignItems,
    gap: styles.controlButtonsContainer?.gap,
    justifyContent: styles.controlButtonsContainer?.justifyContent,
    flexGrow: styles.controlButtonsContainer?.flexGrow,
  }

  const playbackContainerProps = {
    alignItems: styles.playbackContainer?.alignItems,
    flexGrow: styles.playbackContainer?.flexGrow,
    gap: styles.playbackContainer?.gap,
    flexDirection: styles.playbackContainer?.flexDirection,
  }

  const errorContainerProps = {
    alignItems: styles.errorContainer?.alignItems,
    justifyContent: styles.errorContainer?.justifyContent,
  }

  const loadingContainerProps = {
    alignItems: styles.loadingContainer?.alignItems,
    justifyContent: styles.loadingContainer?.justifyContent,
  }

  const transcriptionContainerProps = {
    flexDirection: styles.transcriptionContainer?.flexDirection,
    gap: styles.transcriptionContainer?.gap,
  }

  const playbackSpeedContainerProps = {
    flexDirection: styles.playbackSpeedContainer?.flexDirection,
    justifyContent: styles.playbackSpeedContainer?.justifyContent,
  }

  const SliderComponent = useMemo(() => {
    const clipLengthMillis = playerState.audioData?.milliseconds ?? 0
    const formatPlaybackTime = (millis: number) =>
      tripLeadingZeroFromTime(formatMilliseconds(millis))
    return (
      <Flex css={styles.playbackContainer} {...playbackContainerProps}>
        <Flex width="100%" justifyContent="space-between">
          <Text css={styles.playbackTimestampText}>
            {`${formattedScrubberPosition}`}
          </Text>
          <Text css={styles.playbackTimestampText} textAlign="right">
            {tripLeadingZeroFromTime(
              playerState.audioData?.formattedTime ?? '0:00',
            )}
          </Text>
        </Flex>

        <Slider.Root
          width="100%"
          css={styles.playbackSlider}
          value={[playerPosition]}
          min={0}
          max={playerState.audioData?.milliseconds ?? 100}
          step={seekStepMillis}
          getAriaValueText={({ value }) =>
            t('audioPlayer.playbackPositionValue', {
              position: formatPlaybackTime(value),
              duration: formatPlaybackTime(clipLengthMillis),
              defaultValue: '{{position}} of {{duration}}',
            })
          }
          thumbAlignment="center"
          onValueChange={(details) =>
            handleSliderValueChanged(details.value[0])
          }
          onValueChangeEnd={(details) =>
            handleSliderValueChanged(details.value[0])
          }
        >
          <Slider.Label srOnly>
            {t('audioPlayer.playbackPosition', 'Playback position')}
          </Slider.Label>
          <Slider.Control>
            <Slider.Track css={styles.playbackSliderTrack}>
              <Slider.Range css={styles.playbackSliderFilledTrack} />
            </Slider.Track>
            <Tooltip
              open={isThumbHovered}
              hasArrow
              placement="top"
              label={`${convertCallDurationNoMilliseconds(playerPosition / 1000)}`}
            >
              <Slider.Thumb
                index={0}
                css={styles.playbackSliderThumbContainer}
                onMouseEnter={() => setIsThumbHovered(true)}
                onMouseLeave={() => setIsThumbHovered(false)}
                data-group
              >
                <Slider.HiddenInput />
                <Box css={styles.playbackSliderVerticalBar} />
                <Box css={styles.playbackSliderThumb}>
                  <Box css={styles.playbackSliderThumbInner} />
                </Box>
              </Slider.Thumb>
            </Tooltip>
          </Slider.Control>
        </Slider.Root>
      </Flex>
    )
  }, [
    formattedScrubberPosition,
    playerPosition,
    playerState.audioData,
    isThumbHovered,
  ])

  const noAudio = !hasValue(audioUri)
  const AudioPlayerBodyContent = useMemo(() => {
    if (noAudio) {
      return (
        <Text
          alignSelf="center"
          justifySelf="center"
          {...textStyles[textStyleKeys.Body1_Bold]}
        >
          {t('audioPlayer.noAudio')}
        </Text>
      )
    }

    if (mediaErrored) {
      return (
        <Flex css={styles.errorContainer} {...errorContainerProps}>
          <Text css={styles.errorText}>{t('audioPlayer.mediaError')}</Text>
        </Flex>
      )
    }
    if (!mediaReady) {
      return (
        <Flex css={styles.loadingContainer} {...loadingContainerProps}>
          <LoadingSpinner />
        </Flex>
      )
    }

    const inlineSlider = variant === AudioPlayerVariant.Inline
    return (
      <>
        <Flex css={styles.controlContainer} {...controlContainerProps}>
          {/* Control Buttons */}
          <Flex
            css={styles.controlButtonsContainer}
            {...controlButtonsContainerProps}
          >
            {playerState.muted && (
              <QuickActionButton
                variant={QuickActionButtonVariants.Default_100}
                actionIcon={VolumeX}
                tooltipText={t('audioPlayer.unmute')}
                onClick={() => setAudioMuted(false)}
              />
            )}
            {!playerState.muted && (
              <QuickActionButton
                variant={QuickActionButtonVariants.Default_100}
                actionIcon={Volume2}
                tooltipText={t('audioPlayer.mute')}
                onClick={() => setAudioMuted(true)}
              />
            )}
            <QuickActionButton
              variant={QuickActionButtonVariants.Default_100}
              actionIcon={Rewind}
              tooltipText={t('audioPlayer.rewind10s')}
              onClick={() => scrubFromDelta(-scrubShortcutLengthMillis)}
            />
            {playerState.isPlaying && (
              <QuickActionButton
                variant={QuickActionButtonVariants.Default_200}
                size={Size.lg}
                actionIcon={Pause}
                tooltipText={t('audioPlayer.pause')}
                iconProps={{
                  boxSize: '1.25rem',
                }}
                onClick={() => setAudioPlaying(false)}
              />
            )}
            {!playerState.isPlaying && (
              <QuickActionButton
                variant={QuickActionButtonVariants.Default_200}
                size={Size.lg}
                actionIcon={Play}
                tooltipText={t('audioPlayer.play')}
                iconProps={{
                  boxSize: '1.25rem',
                }}
                onClick={() => setAudioPlaying(true)}
              />
            )}
            <QuickActionButton
              variant={QuickActionButtonVariants.Default_100}
              actionIcon={FastForward}
              tooltipText={t('audioPlayer.fastForward10s')}
              onClick={() => scrubFromDelta(scrubShortcutLengthMillis)}
            />
            {showJumpToAnswerButton && answerTimestamp && (
              <QuickActionButton
                variant={QuickActionButtonVariants.Default_100}
                actionIcon={CornerRightDown}
                tooltipText={t('audioPlayer.jumpToAnswer')}
                onClick={() => scrubToMillis(answerTimestamp)}
              />
            )}
            <DropdownActionButton
              variant={QuickActionButtonVariants.Default_100}
              icon={playbackSpeeds[selectedPlaybackSpeedIndex].icon}
              tooltipText={t('audioPlayer.changePlaybackSpeed')}
              items={playbackSpeeds
                .map((pbs, _) => {
                  return {
                    value: { speed: pbs.speed },
                    content: pbs.speedText,
                    onSelect: () => handleSetSelectedPlaybackSpeed(pbs),
                  }
                })
                .filter((_, i) => i !== selectedPlaybackSpeedIndex)}
              iconProps={{
                boxSize: '2rem',
              }}
              menuOptions={{
                width: '6.25rem',
              }}
            />
          </Flex>
          {/* Playback Slider (Inline variant) */}
          {inlineSlider && SliderComponent}
          {/* Playback Speed */}
          <Flex
            ref={boxRef}
            position="relative"
            css={styles.playbackSpeedContainer}
            {...playbackSpeedContainerProps}
          >
            {showDownloadButton && (
              <QuickActionButton
                variant={QuickActionButtonVariants.Default_100}
                actionIcon={Download}
                tooltipText={t('audioPlayer.downloadAudio')}
                onClick={() => handleDownloadButtonClicked()}
              />
            )}
            {showInlineInfoButton && (
              <QuickActionButton
                variant={QuickActionButtonVariants.Default_100}
                actionIcon={Info}
                tooltipText={t('audioPlayer.seeKeyboardShortcuts')}
                onClick={handleInfoButtonPressed}
              />
            )}
          </Flex>
        </Flex>
        {/* Playback Slider (Stacked and Analytics variants) */}
        {!inlineSlider && SliderComponent}
        {transcriptionText && (
          <Flex
            css={styles.transcriptionContainer}
            {...transcriptionContainerProps}
          >
            <Text as="h2" css={styles.transcriptionTitleText}>
              {t('voicemail.transcriptionTitleText')}
            </Text>
            <Text css={styles.transcriptionBodyText}>{transcriptionText}</Text>
          </Flex>
        )}
      </>
    )
  }, [
    playerState.muted,
    playerState.isPlaying,
    mediaErrored,
    mediaReady,
    playerState.audioData,
    selectedPlaybackSpeedIndex,
    playbackSpeedMenuOpen,
    variant,
    SliderComponent,
    transcriptionText,
  ])

  const playbackRate = playbackSpeeds[selectedPlaybackSpeedIndex].speed
  const shouldShowHeaderContainer =
    !noAudio && !mediaErrored && (hasValue(playerTitle) || showInfoButton)
  return (
    <Flex ref={playerContainerRef} css={styles.outerContainer}>
      <ReactPlayer
        src={audioUri && audioUri !== '' ? audioUri : undefined}
        ref={setPlayerRef}
        height={0}
        width={0}
        playsInline
        playing={playerState.isPlaying}
        onReady={setIsReady}
        onWaiting={setIsBuffering}
        onPlaying={setDoneBuffering}
        onDurationChange={handleDurationChange}
        onTimeUpdate={setProgress}
        onEnded={setAudioEnded}
        onError={onPlayerLoadError}
        muted={playerState.muted}
        volume={playerState.volume}
        playbackRate={playbackRate}
      />
      <Flex css={styles.container} {...containerProps}>
        {shouldShowHeaderContainer && (
          <Flex css={styles.headerContainer} {...headerContainerProps}>
            {playerTitle && <Text css={styles.titleText}>{playerTitle}</Text>}
            <Spacer />
            {showInfoButton && (
              <QuickActionButton
                variant={QuickActionButtonVariants.Default_100}
                size={Size.sm}
                actionIcon={Info}
                tooltipText={t('audioPlayer.seeKeyboardShortcuts')}
                onClick={handleInfoButtonPressed}
              />
            )}
          </Flex>
        )}
        {AudioPlayerBodyContent}
      </Flex>
    </Flex>
  )
}
